import NotificationsManagement from './pages/admin/NotificationsManagement';
import CMSManagement from './pages/admin/CMSManagement';
import ReportsDashboard from './pages/admin/ReportsDashboard';
import SettingsPageAdmin from './pages/admin/SettingsPage';
import UsersManagement from './pages/admin/UsersManagement';
import React, { useEffect, useRef, useState, useContext } from 'react';
import { CircularProgress } from '@mui/material';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import HomePage from './pages/HomePage';
import HomePage2 from './pages/HomePage2';
import BookingModal from './components/BookingModal';
import AdminDashboard from './pages/admin/AdminDashboard';
import PaymentsManagement from './pages/admin/PaymentsManagement';
import PackagesManagement from './pages/admin/PackagesManagement';
import SuperAdminDashboard from './pages/admin/SuperAdminDashboard';
import StorageManagement from './pages/admin/StorageManagement';
import BoothManagement from './pages/admin/BoothManagement';
import SpeakerManagement from './pages/admin/SpeakerManagement';
import StaffDashboard from './pages/staff/StaffDashboard';
import EventsManagement from './pages/admin/EventsManagement';
import BookingManagement from './pages/admin/BookingManagement';
import TicketManagement from './pages/admin/TicketManagement';
import BookingReview from './pages/staff/BookingReview';
import TicketSupport from './pages/staff/TicketSupport';
import StaffCMS from './pages/staff/StaffCMS';
import StaffSpeakersPage from './pages/staff/SpeakersPage';
import StaffBoothPage from './pages/staff/BoothPage';
import StaffPaymentsPage from './pages/staff/PaymentsPage';
import StaffStoragePage from './pages/staff/StoragePage';
import StaffPackagesPage from './pages/staff/PackagesPage';
import NotificationsStaff from './pages/staff/Notifications';
import SettingsPageStaff from './pages/staff/SettingsPage';
import TicketConfirmation from './pages/TicketConfirmation';
import AboutPage from './pages/AboutPage';
import PackagesPage from './pages/PackagesPage';
import SchedulePage from './pages/SchedulePage';
import HighlightsPage from './pages/HighlightsPage';
import VipsPage from './pages/vips';
import VenuesPage from './pages/VenuesPage';
import ContactPage from './pages/ContactPage';
import AccommodationsPage from './pages/AccommodationsPage';
import PrivacyPage from './pages/PrivacyPage';
import Footer from './components/Footer';
import LoginPage from './pages/LoginPage';
import ProtectedRoute from './components/ProtectedRoute';
import ProfilePage from './pages/ProfilePage';
import { SettingsProvider, SettingsContext } from './context/SettingsContext';
import { NotificationProvider } from './context/NotificationContext';
import { ProfileProvider } from './context/ProfileContext';
import PackageDetail from './pages/PackageDetail';

function BrandingUpdater() {
  const settings = useContext(SettingsContext);
  useEffect(() => {
    if (settings?.platformName) {
      document.title = settings.platformName;
    }
    if (settings?.faviconUrl) {
      let link = document.querySelector("link[rel*='icon']");
      if (!link) {
        link = document.createElement('link');
        link.rel = 'icon';
        document.head.appendChild(link);
      }
      link.href = settings.faviconUrl;
    }
  }, [settings]);
  return null;
}

function LoaderOverlay() {
  const settings = useContext(SettingsContext);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const loader = settings?.loader;
    if (!loader || !loader.enabled) return;
    setVisible(true);
    // choose spinner/background based on prefers-color-scheme
    const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    const dur = Number(loader.durationMs) || 800;
    try {
      if (loader.customJS) {
        // execute user JS in relaxed sandbox (caller responsibility)
        // eslint-disable-next-line no-new-func
        new Function(loader.customJS)();
      }
    } catch (e) {
      // ignore custom JS errors
      console.error('Loader customJS error', e);
    }
    const t = setTimeout(() => setVisible(false), dur);
    return () => clearTimeout(t);
  }, [settings]);

  if (!visible || !settings?.loader?.enabled) return null;
  const loader = settings.loader;
  const prefersDark = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
  const spinner = prefersDark ? (loader.spinnerDark || loader.spinnerLight || loader.spinner) : (loader.spinnerLight || loader.spinnerDark || loader.spinner);
  const bg = prefersDark ? (loader.backgroundColorDark || loader.backgroundColor || '#0b1220') : (loader.backgroundColorLight || loader.backgroundColor || '#ffffff');

  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: bg, zIndex: 1400 }}>
      {spinner ? <img src={spinner} alt="loader" style={{ width: 120, height: 120, objectFit: 'contain' }} /> : <CircularProgress />}
      <div style={{ position: 'absolute', bottom: 36, textAlign: 'center', color: prefersDark ? '#fff' : '#000' }}>{loader.message}</div>
    </div>
  );
}

function App() {
  // Notifications are handled by `NotificationProvider` (SSE + REST fallback)

  return (
    <SettingsProvider>
      <NotificationProvider>
        <ProfileProvider>
          <BrandingUpdater />
          <LoaderOverlay />
          <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
            <Routes>
              <Route path="/" element={<HomePage />} />
              <Route path="/home2" element={<HomePage2 />} />
              <Route path="/booking" element={<BookingModal />} />
              {/* Profile route - for all authenticated users */}
              <Route path="/profile" element={<ProtectedRoute allowedRoles={["superadmin", "admin", "staff"]}><ProfilePage /></ProtectedRoute>} />
              {/* Admin routes - only for superadmin, admin */}
              <Route path="/admin" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><SuperAdminDashboard /></ProtectedRoute>} />
              <Route path="/admin/events" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><EventsManagement /></ProtectedRoute>} />
              <Route path="/admin/packages" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><PackagesManagement /></ProtectedRoute>} />
              <Route path="/admin/bookings" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><BookingManagement /></ProtectedRoute>} />
              <Route path="/admin/tickets" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><TicketManagement /></ProtectedRoute>} />
              <Route path="/admin/payments" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><PaymentsManagement /></ProtectedRoute>} />
              <Route path="/admin/notifications" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><NotificationsManagement /></ProtectedRoute>} />
              <Route path="/admin/cms" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><CMSManagement /></ProtectedRoute>} />
              <Route path="/admin/reports" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><ReportsDashboard /></ProtectedRoute>} />
              <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><SettingsPageAdmin /></ProtectedRoute>} />
              <Route path="/admin/users" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><UsersManagement /></ProtectedRoute>} />
              <Route path="/admin/storage" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><StorageManagement /></ProtectedRoute>} />
              <Route path="/admin/booths" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><BoothManagement /></ProtectedRoute>} />
              <Route path="/admin/speakers" element={<ProtectedRoute allowedRoles={["superadmin", "admin"]}><SpeakerManagement /></ProtectedRoute>} />
              {/* Staff routes - only for staff */}
              <Route path="/staff" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><StaffDashboard /></ProtectedRoute>} />
              <Route path="/staff/bookings" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><BookingReview /></ProtectedRoute>} />
              <Route path="/staff/tickets" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><TicketSupport /></ProtectedRoute>} />
              <Route path="/staff/notifications" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><NotificationsStaff /></ProtectedRoute>} />
              <Route path="/staff/cms" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><StaffCMS /></ProtectedRoute>} />
              <Route path="/staff/settings" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><SettingsPageStaff /></ProtectedRoute>} />
              <Route path="/staff/speakers" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><StaffSpeakersPage /></ProtectedRoute>} />
              <Route path="/staff/booths" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><StaffBoothPage /></ProtectedRoute>} />
              <Route path="/staff/payments" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><StaffPaymentsPage /></ProtectedRoute>} />
              <Route path="/staff/storage" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><StaffStoragePage /></ProtectedRoute>} />
              <Route path="/staff/packages" element={<ProtectedRoute allowedRoles={["staff", "booking", "speaker"]}><StaffPackagesPage /></ProtectedRoute>} />
              <Route path="/ticket-confirmation" element={<TicketConfirmation />} />
              <Route path="/about" element={<AboutPage />} />
              <Route path="/packages" element={<PackagesPage />} />
              <Route path="/schedule" element={<SchedulePage />} />
              <Route path="/highlights" element={<HighlightsPage />} />
              <Route path="/vips" element={<VipsPage />} />
              <Route path="/venues" element={<VenuesPage />} />
              <Route path="/accommodations" element={<AccommodationsPage />} />
              <Route path="/accommodation" element={<Navigate to="/accommodations" replace />} />
              <Route path="/accomodation" element={<Navigate to="/accommodations" replace />} />
              <Route path="/accomodations" element={<Navigate to="/accommodations" replace />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/packages/:id" element={<PackageDetail />} />
            </Routes>
          </Router>
        </ProfileProvider>
      </NotificationProvider>
    </SettingsProvider>
  );
}

export default App;
